import CloseIcon from '@mui/icons-material/Close';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	IconButton,
	Typography,
} from '@mui/material';
import type { CustomDialogProps } from './types';

export default function CustomDialog({
	children,
	dialogTitle,
	Open,
	otherProps,
	backdropClick = false,
	handleClose = () => {},
	showCloseIcon = false,
	dialogActions,
	otherTitlePros,
	dialogTitleJsx,
	placement = 'center',
	width,
}: CustomDialogProps) {
	const isRightPlacement = placement === 'right';

	return (
		<Dialog
			{...otherProps}
			open={Open}
			onClose={() => {
				if (backdropClick) handleClose();
			}}
			sx={{
				/* `children` (the body) is always a pre-padded box of its own
           (`.card-bd`, in every caller), same as `dialogActions` below —
           without this, MUI's own 20px/24px `DialogContent` padding stacks
           on top of `.card-bd`'s, doubling the inset on every side and
           making the body narrower than the header/footer sitting above and
           below it. */
				'& .MuiDialogContent-root': { border: 'none', padding: 0 },
				/* `dialogActions` is always a pre-padded footer box of its own
           (`.card-ft`, in every caller) — `DialogActions`'s own default
           padding insets it from the dialog's edges, which is why its
           `border-top` separator only spanned part of the width instead of
           going edge to edge. */
				'& .MuiDialogActions-root': { padding: 0 },
				/* `.modal-panel` (the `center`-placement `PaperProps.className`) is
           fixed at `min(720px, 100%)` — right for a form or a table, and the
           reason a one-sentence confirm dialog used to render as a mostly
           empty 720px-wide box. An explicit `width` overrides it either way,
           not just for the `right` drawer. */
				...(width !== undefined &&
					!isRightPlacement && { '& .MuiDialog-paper': { width } }),
				...(isRightPlacement && {
					'& .MuiDialog-container': { justifyContent: 'flex-end' },
					'& .MuiDialog-paper': {
						margin: 0,
						height: '100%',
						maxHeight: '100%',
						borderRadius: 0,
						width: width ?? 480,
					},
				}),
				...otherProps?.sx,
			}}
		>
			{dialogTitle && dialogTitle.length >= 1 && (
				<DialogTitle sx={{ m: 0, p: 2 }} id="custom-dialog-title">
					<Grid2
						container
						justifyContent="space-between"
						alignItems="center"
					>
						<Grid2 size={{ xs: showCloseIcon ? 8 : 12 }}>
							<Typography
								fontWeight={600}
								fontSize="var(--fs-section)"
								sx={otherTitlePros}
							>
								{dialogTitle}
							</Typography>
						</Grid2>
						{showCloseIcon && (
							<Grid2>
								<IconButton onClick={handleClose} size="small">
									<CloseIcon />
								</IconButton>
							</Grid2>
						)}
					</Grid2>
				</DialogTitle>
			)}

			{dialogTitleJsx}
			<DialogContent dividers>{children}</DialogContent>
			{dialogActions && <DialogActions>{dialogActions}</DialogActions>}
		</Dialog>
	);
}
